import { useRef, useState, useEffect } from 'react'
import { Truck, ShieldCheck, Headphones, User, UserPlus, LogOut, UserCircle, ChevronDown } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useTopBarItems } from '../../config/nav.config'
import { useApiAuth, useLogoutMutation } from '@/modules/auth-api'
import { useCartStore } from '@/modules/cart'

const ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  'truck': Truck,
  'shield-check': ShieldCheck,
  'headphones': Headphones,
}

export function TopBar() {
  const items = useTopBarItems()
  const user = useApiAuth((s) => s.user)
  const isAuthed = useApiAuth((s) => s.isAuthed)
  const logoutMut = useLogoutMutation()
  const clearCart = useCartStore((s) => s.clear)
  const navigate = useNavigate()

  const [menuOpen, setMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false)
    }
    window.addEventListener('mousedown', onClick)
    return () => window.removeEventListener('mousedown', onClick)
  }, [])

  const handleLogout = async () => {
    await logoutMut.mutateAsync()
    clearCart()
    setMenuOpen(false)
    navigate('/', { replace: true })
  }

  return (
    <div className="bg-brand-600 text-white text-sm">
      <div className="max-w-[1440px] mx-auto px-6 h-10 flex items-center justify-between">
        <div className="flex items-center gap-6 overflow-x-auto">
          {items.map((item) => {
            const Icon = ICONS[item.icon]
            return (
              <div key={item.label} className="flex items-center gap-2 whitespace-nowrap">
                {Icon && <Icon className="w-4 h-4" />}
                <span>{item.label}</span>
              </div>
            )
          })}
        </div>

        {isAuthed && user ? (
          <div className="relative shrink-0" ref={menuRef}>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="flex items-center gap-2 h-8 px-3 rounded-full hover:bg-white/15 transition"
            >
              <UserCircle className="w-4 h-4" />
              <span className="font-medium hidden sm:inline">{user.fullName.split(' ').slice(-1)[0] || user.username}</span>
              <ChevronDown className="w-3.5 h-3.5 opacity-80" />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-2 w-56 bg-surface border border-border rounded-lg shadow-hover py-2 z-40 text-text">
                <div className="px-4 py-2 border-b border-border">
                  <div className="text-sm font-semibold">{user.fullName}</div>
                  <div className="text-xs text-muted">{user.email}</div>
                </div>
                {user.role === 'ADMIN' && (
                  <Link
                    to="/admin"
                    onClick={() => setMenuOpen(false)}
                    className="flex items-center gap-2.5 px-4 h-10 text-sm hover:bg-slate-50 transition"
                  >
                    <UserCircle className="w-4 h-4 text-brand-600" /> Trang quản trị
                  </Link>
                )}
                <Link
                  to="/tai-khoan"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2.5 px-4 h-10 text-sm hover:bg-slate-50 transition"
                >
                  <User className="w-4 h-4" /> Tài khoản của tôi
                </Link>
                <Link
                  to="/don-hang"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-2.5 px-4 h-10 text-sm hover:bg-slate-50 transition"
                >
                  <Truck className="w-4 h-4" /> Đơn hàng của tôi
                </Link>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-2.5 px-4 h-10 text-sm text-danger hover:bg-danger/5 transition"
                >
                  <LogOut className="w-4 h-4" /> Đăng xuất
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-5 shrink-0">
            <Link to="/dang-nhap" className="flex items-center gap-1.5 hover:underline">
              <User className="w-4 h-4" /> Đăng nhập
            </Link>
            <Link to="/dang-ky" className="flex items-center gap-1.5 hover:underline">
              <UserPlus className="w-4 h-4" /> Đăng ký
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}
